import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import BrowserStorage from 'api/utils/browserStorage';

import { sendRetenoAnalyticsRequest } from 'store/analytics/actions';
import { getDeepLinkRequest } from 'store/deepLink/actions';
import { selectDeepLink } from 'store/deepLink/selectors';
import { selectCancellationCandidates } from 'store/subscriptions/selectors';
import { selectCurrentUser } from 'store/user/selectors';

import { DOWNLOAD_APP_EVENT_LABELS } from 'constants/analytics';
import { Platform, PLATFORM_BY_KEY } from 'constants/api';
import { YG_CABINET_DEEPLINK_CLICK } from 'constants/dataDog';

import {
    sendAnalyticCancelOfferClick,
    sendAnalyticDeeplinkLoadClick,
    sendAnalyticDownloadAppClick,
    trackScreenLoadCancelOffer,
} from 'services/Analytics';
import { trackScreenLoad } from 'services/Analytics/trackers/mainTrackers';
import { trackDatadogRumAction } from 'services/DataDog';

import { useDispatch, useSelector } from 'hooks/store';

import isMobile from 'helpers/isMobile';
import { LS_KEYS } from 'helpers/localStorage';
import { getValuesByKey } from 'helpers/utils';

import { CustomPrimaryButton } from 'components/Buttons';

import { CancelOfferAction } from 'types/subscription';

import { IInstallAppSteps } from '../types';

import DownloadWithTabletLinks from './components/DownloadWithTabletLinks';
import StepProgress from './components/StepProgress';
import { useGetSteps } from './useGetSteps';

import * as S from './styled';

const InstallAppSteps = ({
    downloadButtonText,
    customLink,
    cancelOfferName = null,
    showProgressBar = false,
    startStepsFrom = 1,
}: IInstallAppSteps) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const deepLinkFromServer = useSelector(selectDeepLink);
    const { id, email } = useSelector(selectCurrentUser);

    const deepLinkUrl = customLink || deepLinkFromServer;

    const isAndroid = isMobile.android();
    const isIos = isMobile.iOS();
    const cancellationCandidates = useSelector(selectCancellationCandidates);
    const isCancelOffer = Boolean(cancelOfferName);

    const subscription_id = getValuesByKey(cancellationCandidates, 'external_id');

    const [isAppDownloaded, setIsAppDownloaded] = useState(BrowserStorage.getItem('isAppDownloaded'));
    const [isUserFirstTimeOnMainPageAfterOB, setIsUserFirstTimeOnMainPageAfterOB] = useState<boolean>(false);

    useEffect(() => {
        const isFirstVisitAfterOB = Boolean(localStorage.getItem(LS_KEYS.isUserFirstTimeOnMainPageAfterOB));

        setIsUserFirstTimeOnMainPageAfterOB(isFirstVisitAfterOB);

        return () => {
            isFirstVisitAfterOB && localStorage.removeItem(LS_KEYS.isUserFirstTimeOnMainPageAfterOB);
        };
    }, []);

    useEffect(() => {
        if (!deepLinkUrl) dispatch(getDeepLinkRequest());
    }, [deepLinkUrl, dispatch]);

    useEffect(() => {
        isCancelOffer
            ? trackScreenLoadCancelOffer({ eventLabel: { subscription_id, offer: cancelOfferName } })
            : trackScreenLoad();

        isUserFirstTimeOnMainPageAfterOB &&
            !isCancelOffer &&
            dispatch(
                sendRetenoAnalyticsRequest({
                    event_type_key: 'main__screen__load',
                    event_params: {
                        externalCustomerId: id,
                        email,
                        time: new Date().toISOString(),
                        platform: PLATFORM_BY_KEY[Platform.Web],
                    },
                })
            );
    }, [isCancelOffer, dispatch, subscription_id, cancelOfferName, id, email, isUserFirstTimeOnMainPageAfterOB]);

    useEffect(() => {
        deepLinkUrl && sendAnalyticDeeplinkLoadClick();
    }, [deepLinkUrl]);

    const handleDownloadApp = (eventLabel: string, isDeeplinkBtn = true) => {
        setIsAppDownloaded(true);
        BrowserStorage.setItem('isAppDownloaded', true);

        !isDeeplinkBtn && window.open(deepLinkUrl, '_blank', 'noopener,noreferrer');

        if (isMobile.any()) {
            trackDatadogRumAction(YG_CABINET_DEEPLINK_CLICK, { eventLabel });
        }

        isCancelOffer
            ? sendAnalyticCancelOfferClick({
                  eventLabel: { subscription_id, offer: cancelOfferName },
                  actionEvent: CancelOfferAction.Accept,
              })
            : sendAnalyticDownloadAppClick(eventLabel);

        isUserFirstTimeOnMainPageAfterOB &&
            !isCancelOffer &&
            dispatch(
                sendRetenoAnalyticsRequest({
                    event_type_key: 'main__click',
                    event_params: {
                        event_label: eventLabel,
                        externalCustomerId: id,
                        email,
                        time: new Date().toISOString(),
                        platform: PLATFORM_BY_KEY[Platform.Web],
                    },
                })
            );
    };

    const steps = useGetSteps({
        isAppDownloaded,
        isIos,
        isAndroid,
        deepLinkUrl,
        handleDownloadApp,
    });

    return (
        <>
            {showProgressBar && <StepProgress isAppDownloaded={isAppDownloaded} />}

            <S.StepContainer $mb={24}>
                {steps.map((element, index) => element(startStepsFrom + index))}

                <S.MobileWrapper>
                    <CustomPrimaryButton
                        dataLocator="deeplinkBtn"
                        onClick={() => handleDownloadApp(DOWNLOAD_APP_EVENT_LABELS.general_bot, false)}
                    >
                        {t(downloadButtonText ? downloadButtonText : 'main.button')}
                    </CustomPrimaryButton>
                </S.MobileWrapper>
            </S.StepContainer>

            <DownloadWithTabletLinks handleLinkDownloadApp={handleDownloadApp} />
        </>
    );
};

export default InstallAppSteps;
